import * as React from 'react';
import { ChangeEvent, useRef } from 'react';

import { Button } from '@rmwc/button';
import '@rmwc/button/styles';
import { SheetReader } from '../sheet_reader';

import * as deepEqual from 'fast-deep-equal';
import { addBoxes } from '../api';
import { useKeycloak } from '@react-keycloak/web';

import { DateTime } from 'luxon';

const getLogListEntries = function*(data: string) {
  const lines = data.split('\n');
  let start = 0;

  while(!lines[start].startsWith('Box Snr')) {
    start += 1;

    if(start > lines.length) {
      return;
    }
  }

  start += 1;

  for(let i = start; i < lines.length; ++i) {
    const line = lines[i].trim();

    if(line === '') {
      continue;
    }

    const parts = line.match(/([0-9\-]+)\s*,\s*(\d+)\s*,\s*(\w+)\s+(.*)/);

    if(parts == null) {
      throw new Error("Line with invalid format: " + line);
    }

    const boxId = parts[1];
    const controllerId = parts[2];
    const whenStr = parts[4].replace(': ', ':');
    const when = DateTime.fromFormat(whenStr, 'dd.MM.yy HH:mm:ss');

    if(!when.isValid) {
      throw new Error("Unable to parse date on line: " + line);
    }

    yield { boxId, controllerId, when };
  }
};

const loadSerials = (file: File): Promise<Array<[string,string,DateTime]>> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = async (loadEvent) => {
      try {
        const data = fileReader.result;

        interface MappingEntry {
          controllerId: string;
          when: DateTime;
        };

        const mapping = new Map<string,MappingEntry[]>();
        const controllerMap = new Map<string,string>();

        for(const { boxId, controllerId, when } of getLogListEntries(data as string)) {
          // did we see the controller id before?

          const oldBox = controllerMap.get(controllerId);

          if(oldBox != null) {
            const oldBoxControllers = mapping.get(oldBox)!;

            if(oldBox === boxId) {
              console.log(`Repeat mapping of ${controllerId} into ${boxId}`);
            } else if(oldBoxControllers.length === 1) {
              console.log(`Moving controller ${controllerId} from box ${oldBox} to ${boxId}`);
            } else {
              console.log(`Probably resolving duplicate box ${oldBox} by moving controller ${controllerId} to ${boxId}`);
            }

            // remove from old box, might let older mapping get active again
            mapping.set(oldBox, oldBoxControllers.filter((entry) => entry.controllerId != controllerId));
          }

          // remember where we put that controller

          controllerMap.set(controllerId, boxId);

          // append controller to known mappings of that box

          const boxMapping = mapping.get(boxId);

          const entry = {
            controllerId,
            when,
          };

          if(boxMapping != null) {
            boxMapping.push(entry);
          } else {
            mapping.set(boxId, [entry]);
          }
        }

        // convert history entries into actual mapping

        const entries = Array.from(mapping.entries())
          .filter(([box, controllers]) => controllers.length > 0)
          .map(([box, controllers]): [string,string,DateTime] => {
            if(controllers.length > 1) {
              console.log(`More than one controller for box ${box}`);
            }

            const entry = controllers[controllers.length-1];
            return [box, entry.controllerId, entry.when];
          });

        resolve(entries);
      } catch(err) {
        reject("Unable to parse file");
        console.log(err);
      }
    };

    fileReader.onerror = () => {
      reject("Unable to load file from disk");
    };

    fileReader.readAsBinaryString(file);
  });
}

export const Boxes: React.SFC = () => {
  const { keycloak } = useKeycloak();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    console.log(files);

    if(files == null || files.length == 0) {
      return;
    }

    try {
      const fileCount = files.length;

      const res = await Promise.all(Array.from(files).map((file) => {
        return loadSerials(file);
      }));

      const ids = res.flat();

      const token = keycloak.token;

      if(token == null) {
        throw new Error("No authorization token found");
      }

      const changed = await addBoxes(ids, token);
      alert(`Imported ${changed} of ${ids.length} serial pairs from ${fileCount} file(s)`);
    } catch(err) {
      alert(err);
      console.log(err);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return <div className="box_import">
    <p>Add controllers to the database:</p>
    <Button className="margin_top" raised onClick={handleClick}>Add Controller Serials</Button>
    <input style={{ display: 'none' }} ref={fileInputRef} type="file" value="" onChange={load} multiple />
  </div>;
};
